import { Injectable } from '@angular/core';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';
import { FormControlService } from '../form-control.service';
import { FormControlUtils } from '../utils/form-control.utils';

@Injectable()
export class ChecklistFormControlService {
  private readonly _destroy = new Subject<void>()

  readonly listItemsSubject = new BehaviorSubject<{ text: string, value: any }[]>([])
  readonly canUserDisableSubject = new BehaviorSubject<boolean>(true);
  readonly disabledSubject = new BehaviorSubject<boolean>(false);

  readonly selectedValuesSubject = new BehaviorSubject<any[]>(null)
  get selectedValues() {
    return this.selectedValuesSubject.value;
  }
  readonly allSelectedObs = combineLatest([
    this.listItemsSubject,
    this.selectedValuesSubject
  ])
    .pipe(
      filterAllTruthyInTuple(),
      map(([listItems, selectedValues]) => listItems.length === listItems.filter((item => selectedValues?.includes(item.value))).length)
    )

  readonly showSelectAllObs = this.listItemsSubject.pipe(map(listItems => listItems.length > 1));

  readonly formControlSubject = this._formControlService.formControlSubject
  readonly listItemsSelectedObs = combineLatest([
    this.listItemsSubject,
    this.selectedValuesSubject
  ])
    .pipe(
      filter(([listItems]) => !!listItems),
      map(([listItems, value]) => listItems.map((listItem) => ({ ...listItem, selected: value?.includes(listItem.value) })))
    )


  readonly selectedItemsSubject = subjectFromObs({
    initialValue: [],
    observable: this.listItemsSelectedObs.pipe(map(listItems => listItems.filter(listItem => listItem.selected))),
    until: this._destroy
  })


  constructor(
    private readonly _formControlService: FormControlService
  ) {

    this.selectedValuesSubject.pipe(
      filter(Boolean),
      takeUntil(this._destroy),
      map((selectedValues) => !!selectedValues?.length),
      distinctUntilChanged(),

      filter(Boolean),
    )
      .subscribe(() => {
        this.disabledSubject.next(false);
      })


    combineLatest([
      this._formControlService.valueSubject,
      this.disabledSubject
    ])
      .pipe(
        filter(([value, disabled]) => value && !disabled),
        takeUntil(this._destroy)
      )
      .subscribe(([value]) => {

        if (!value || value === this.selectedValues) {
          return;
        }

        this.selectedValuesSubject.next(value);
      })

    combineLatest([this.selectedValuesSubject, this.disabledSubject])
      .pipe(
        filter(([selectedValues]) => !!selectedValues),
        map(([selectedValues, disabled]) => {
          if (disabled) {
            return null;
          }

          return selectedValues;
        }),
        takeUntil(this._destroy)
      )
      .subscribe((value) => {
        if (value === this._formControlService.valueSubject.value) {
          return;
        }

        this._formControlService.valueSubject.next(value)
      })

    this.formControlSubject
      ?.pipe(
        filter(Boolean),
        take(1),
        takeUntil(this._destroy)
      )
      .subscribe((formControl) => {

        const optionsInstance = formControl.options;
        const disposables = FormControlUtils.mapControlOptionsToService(optionsInstance, this)

        if (!disposables) {
          return;
        }
      })


    combineLatest([this.canUserDisableSubject, this._formControlService.valueSubject])
      .pipe(
        takeUntil(this._destroy),
        map(([canDisable, formValue]) => canDisable && !formValue?.length),
        distinctUntilChanged()
      )
      .subscribe((defaultDisable) =>
        this.disabledSubject.next(defaultDisable)
      )

  }

  selectAll() {
    this.selectedValuesSubject.next(this.listItemsSubject.value.map(({ value }) => value));
  }

  setChecked(value: any, checked: boolean) {
    const isAlreadySelected = this.selectedValues?.includes(value);

    if (!isAlreadySelected && checked) {
      this.selectedValuesSubject.next([
        ...(this.selectedValues || []),
        value
      ])

      return;
    }

    this.selectedValuesSubject.next(this.selectedValues?.filter(innerValue => innerValue !== value) || [])
  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }
}
